import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import { fetchTrustSafetyDashboard } from "../../../services/automapTrustSafetyApi";
import { trackTrustSafetyEvent } from "../../../constants/automapTrustSafetyConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { TrustSafetyNav } from "./trustSafetyShared";

function Metric({ label, value, href }) {
  const body = (
    <div className="admin-dashboard__metric-card">
      <span className="admin-dashboard__metric-label">{label}</span>
      <strong className="admin-dashboard__metric-value">{value ?? "—"}</strong>
    </div>
  );
  return href ? <Link to={href}>{body}</Link> : body;
}

function AdminTrustSafetyDashboardPageContent() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTrustSafetyDashboard();
      setSummary(data.summary);
      trackTrustSafetyEvent("trust_safety_dashboard_viewed");
    } catch (err) {
      setError(err.message || "No se pudo cargar confianza y seguridad.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = summary || {};

  return (
    <AdminPageLayout>
      <AdminAutomotiveNav active="trust-safety" />
      <AdminPageHeader
        eyebrow="AutoMapa · Confianza"
        title="Confianza y seguridad"
        description="Reportes de compradores, casos abiertos y acciones de protección."
        navigation={<TrustSafetyNav active="dashboard" />}
      />
      <main className="admin-console__main">
        {error ? <AdminStatePanel variant="empty" message={error} onRetry={load} retryLabel="Reintentar" /> : null}
        {loading ? <AdminStatePanel variant="empty" message="Cargando…" /> : (
          <>
            <div className="admin-dashboard__metric-grid">
              <Metric label="Casos abiertos" value={s.openCases} href="/admin/trust-safety/cases" />
              <Metric label="Reportes nuevos" value={s.newReports} href="/admin/trust-safety/reports" />
              <Metric label="Críticos" value={s.criticalCases} href="/admin/trust-safety/cases?severity=critical" />
              <Metric label="Alta severidad" value={s.highCases} href="/admin/trust-safety/cases?severity=high" />
              <Metric label="Esperando dealer" value={s.waitingOnDealer} />
              <Metric label="Esperando comprador" value={s.waitingOnBuyer} />
              <Metric label="Resueltos (mes)" value={s.resolvedThisMonth} />
              <Metric label="Listings sospechosos" value={s.suspiciousListingCount} href="/admin/trust-safety/listings" />
              <Metric label="Dealers repetidos" value={s.dealersWithRepeatReports} href="/admin/trust-safety/dealers" />
            </div>
            <h3 className="admin-dashboard__section-title" style={{ marginTop: "1.5rem" }}>Casos prioritarios</h3>
            {(s.priorityCases || []).length ? (
              <ul className="admin-dashboard__queue-list">
                {s.priorityCases.map((c) => (
                  <li key={c.id}>
                    <span>{c.title}</span>
                    <Link to={`/admin/trust-safety/cases/${c.id}`}>Ver →</Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Sin casos prioritarios.</p>
            )}
          </>
        )}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminTrustSafetyDashboardPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminTrustSafetyDashboardPageContent />
    </RequirePermission>
  );
}
